<template>
  <div class="modal fade" tabindex="-1" ref="modal_calibracao">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
      <div class="modal-content">
        <div class="modal-header bg-primary">
          <h3 class="modal-title text-white mb-0">
            Calibração do Dispositivo
          </h3>
          <button
            type="button"
            class="btn-close"
            @click="closeModal"
            aria-label="Fechar"
          ></button>
        </div>
        <div class="modal-body">
          <div class="alert alert-warning" role="alert">
            <i class="bx bx-info-circle"></i>
            <strong>Atenção:</strong> Os fatores de calibração são multiplicadores aplicados aos dados do dispositivo.
            <br>
            Se não houver nenhuma calibração cadastrada, o valor padrão (1.0) será considerado para todos os fatores.
          </div>

          <!-- Seletor de Sensor -->
          <div class="sensor-selector-card mb-4">
            <h5>Sensor</h5>
            <VueMultiselect
              v-model="selectedSensor"
              :options="sensorOptions"
              :searchable="true"
              :show-labels="false"
              :placeholder="loadingSensores ? 'Carregando sensores...' : 'Selecione um sensor'"
              label="nome"
              track-by="linkname"
              :max-height="300"
              @select="onSensorSelect"
              :disabled="loadingSensores"
            >
              <template v-slot:noOptions>
                Nenhum sensor calibrável
              </template>
              <template v-slot:noResult>
                Nenhum sensor encontrado
              </template>
            </VueMultiselect>
            <small v-if="!loadingSensores && !sensorOptions.length" class="text-muted d-block mt-2">
              Nenhum sensor calibrável para este dispositivo.
            </small>
          </div>

          <!-- Calibração Ativa (do sensor selecionado) -->
          <div v-if="selectedSensor && calibracaoAtivaSensor" class="current-calibration-card mb-4">
            <h5>Calibração Ativa</h5>
            <div class="row">
              <div
                v-for="fator in calibracaoAtivaSensor.fatores"
                :key="'ativa-' + fator.fator"
                class="col-md-3 col-12 mb-3"
              >
                <label class="form-label">{{ fator.label }}</label>
                <input
                  type="text"
                  class="form-control"
                  :value="fator.valor"
                  disabled
                />
              </div>
              <div class="col-12">
                <small class="text-muted">
                  Registrado em:
                  {{ new Date(calibracaoAtivaSensor.dataRegistro).toLocaleString('pt-BR') }}
                </small>
              </div>
            </div>
          </div>

          <!-- Novo Formulário de Calibração (dinâmico por sensor) -->
          <div v-if="selectedSensor" class="new-calibration-form">
            <h5>Nova Calibração</h5>
            <div class="row">
              <div
                v-for="fator in selectedSensor.calibrationFactors"
                :key="'input-' + fator.fator"
                class="col-md-3 col-12 mb-3"
              >
                <label class="form-label">
                  {{ fator.label }} <span class="text-danger">*</span>
                </label>
                <input
                  type="number"
                  step="0.000001"
                  class="form-control"
                  v-model.number="fatoresValores[fator.fator]"
                  placeholder="1.0"
                />
              </div>
            </div>

            <div class="d-flex justify-content-end mt-3">
              <button
                class="btn btn-success"
                @click="salvarCalibracao"
                :disabled="loading || !isFormValid"
              >
                <span v-if="loading">
                  <i class="bx bx-loader-alt bx-spin"></i> Salvando...
                </span>
                <span v-else>
                  <i class="bx bx-save"></i> Salvar Calibração
                </span>
              </button>
            </div>
          </div>

          <!-- Histórico de Calibrações -->
          <div class="historico-container mt-4">
            <h5>Histórico de Calibrações</h5>
            <Datatable
              v-if="qrCodeId"
              ref="datatableHistorico"
              :fields="fieldsHistorico"
              url="/Calibracao/Historico"
              :where="whereFilter"
              :mobileCardMode="true"
              @delete="confirmarDelete"
            >
              <template v-slot:empty>
                <div class="text-center my-5">
                  <p>Nenhuma calibração encontrada</p>
                </div>
              </template>
            </Datatable>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal de Confirmação de Exclusão -->
  <div class="modal fade" tabindex="-1" ref="modal_delete">
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header bg-primary">
          <h5 class="modal-title text-white mb-2">Deletar Calibração</h5>
          <button
            type="button"
            class="btn-close"
            @click="cancelarDelete"
          ></button>
        </div>
        <div class="modal-body">
          <span>
            Deseja realmente excluir esta calibração registrada em
            <strong>{{ deleteItemData ? new Date(deleteItemData.dataRegistro).toLocaleString('pt-BR') : '' }}</strong>?
          </span>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="cancelarDelete"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-danger"
            @click="executarDelete"
            :disabled="deleting"
          >
            <div
              class="spinner-border spinner-border-sm text-white me-2"
              role="status"
              v-if="deleting"
            >
              <span class="visually-hidden">Loading...</span>
            </div>
            {{ deleting ? "Excluindo..." : "Excluir" }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <Alert ref="alert"></Alert>
</template>

<script>
import api from '@/mixin/api.js';
import Datatable from "../crud/Datatable.vue";
import Alert from "../Alert.vue";
import VueMultiselect from 'vue-multiselect';

export default {
  name: 'CalibracaoModal',
  mixins: [api],
  components: {
    Datatable,
    Alert,
    VueMultiselect
  },
  data() {
    return {
      qrCodeId: null,
      loading: false,
      loadingSensores: false,
      deleting: false,
      deleteItemId: null,
      deleteItemData: null,
      // Sensor selecionado e opções (apenas sensores com fatores calibráveis)
      selectedSensor: null,
      sensorOptions: [],
      // Valores dos fatores da nova calibração, indexados pelo número do fator (enum)
      fatoresValores: {},
      // Lista "porSensor" retornada pelo GET /Calibracao/{qrCodeId}
      porSensor: [],
      fieldsHistorico: [
        {
          name: "Data de Registro",
          value: "dataRegistro",
          format: (v, d) => {
            if (v != null) {
              return new Date(v).toLocaleString('pt-BR');
            } else {
              return "-";
            }
          },
          style: { textAlign: "center", width: "200px" }
        },
        {
          name: "Sensor",
          value: "sensorName",
          format: (v) => v ? v : "-",
          style: { textAlign: "center", width: "140px" }
        },
        {
          name: "Fator Chuva",
          value: "fatorChuva",
          format: (v) => v ? v.toFixed(6) : "1.0",
          style: { textAlign: "center", width: "120px" }
        },
        {
          name: "Fator Pressão",
          value: "fatorPressao",
          format: (v) => v ? v.toFixed(6) : "1.0",
          style: { textAlign: "center", width: "120px" }
        },
        {
          name: "Vel. Vento",
          value: "fatorVelocidadeVento",
          format: (v) => v ? v.toFixed(6) : "1.0",
          style: { textAlign: "center", width: "120px" }
        },
        {
          name: "Rajada Vento",
          value: "fatorRajadaVento",
          format: (v) => v ? v.toFixed(6) : "1.0",
          style: { textAlign: "center", width: "120px" }
        },
        {
          name: "Status",
          value: "ativa",
          format: (v, d) => {
            return v
              ? '<span class="badge bg-success">Ativa</span>'
              : '';
          },
          style: { textAlign: "center", width: "100px" }
        },
        {
          name: "Ação",
          type: "action",
          orderby: false,
          value: "action",
          edit: false,
          delete: true,
          style: { width: "80px", minWidth: "80px", textAlign: "center" }
        }
      ]
    };
  },
  computed: {
    whereFilter() {
      const filter = {
        QRCodeId: {
          value: this.qrCodeId
        }
      };
      return filter;
    },
    // Calibração ativa (porSensor) correspondente ao sensor selecionado
    calibracaoAtivaSensor() {
      if (!this.selectedSensor) return null;
      return this.porSensor.find(
        p => p.sensorName === this.selectedSensor.linkname
      ) || null;
    },
    isFormValid() {
      if (!this.selectedSensor) return false;
      const fatores = this.selectedSensor.calibrationFactors || [];
      if (!fatores.length) return false;
      return fatores.every(f => {
        const valor = this.fatoresValores[f.fator];
        return typeof valor === 'number' && valor > 0;
      });
    }
  },
  methods: {
    open(qrCodeId) {
      this.qrCodeId = qrCodeId;
      this.selectedSensor = null;
      this.sensorOptions = [];
      this.fatoresValores = {};
      this.porSensor = [];
      this.carregarSensores();
      this.carregarCalibracoes();
      $(this.$refs.modal_calibracao).modal({
        backdrop: "static",
        keyboard: false,
      });
    },
    closeModal() {
      $(this.$refs.modal_calibracao).modal("hide");
    },
    // Carrega os sensores do dispositivo e mantém apenas os que têm fatores calibráveis
    async carregarSensores() {
      if (!this.qrCodeId) {
        this.sensorOptions = [];
        return;
      }

      this.loadingSensores = true;
      try {
        const response = await this.urlPost('/Station/GetSensorByQrCodeId/' + this.qrCodeId);
        const itens = (response.data && response.data.itens) || [];
        this.sensorOptions = itens
          .filter(sensor => (sensor.calibrationFactors || []).length > 0)
          .map(sensor => ({
            linkname: sensor.linkname,
            nome: sensor.nome,
            calibrationFactors: sensor.calibrationFactors || []
          }));
      } catch (error) {
        console.error('Erro ao carregar sensores do dispositivo:', error);
        this.sensorOptions = [];
      } finally {
        this.loadingSensores = false;
      }
    },
    // Carrega as calibrações (ativa por sensor) do dispositivo
    async carregarCalibracoes() {
      if (!this.qrCodeId) return;

      try {
        const response = await this.urlGet(`/Calibracao/${this.qrCodeId}`);

        if (response.data) {
          this.porSensor = response.data.porSensor || [];
        }

        // Se já há sensor selecionado, repreenche os inputs com a calibração ativa
        if (this.selectedSensor) {
          this.preencherFatores(this.selectedSensor);
        }

        // Atualiza a datatable
        this.$nextTick(() => {
          if (this.$refs.datatableHistorico) {
            this.$refs.datatableHistorico.update();
          }
        });
      } catch (error) {
        console.error('Erro ao carregar calibrações:', error);
      }
    },
    onSensorSelect(sensor) {
      if (!sensor) {
        this.fatoresValores = {};
        return;
      }
      this.preencherFatores(sensor);
    },
    // Preenche os inputs de fatores com base na calibração ativa do sensor (ou 1.0)
    preencherFatores(sensor) {
      const ativa = this.porSensor.find(p => p.sensorName === sensor.linkname);
      const valores = {};
      (sensor.calibrationFactors || []).forEach(f => {
        let valor = 1.0;
        if (ativa && Array.isArray(ativa.fatores)) {
          const ativaFator = ativa.fatores.find(af => af.fator === f.fator);
          if (ativaFator && ativaFator.valor != null) {
            valor = ativaFator.valor;
          }
        }
        valores[f.fator] = valor;
      });
      this.fatoresValores = valores;
    },
    async salvarCalibracao() {
      if (!this.selectedSensor) {
        this.$refs.alert.show(
          "Validação",
          "Selecione um sensor para calibrar",
          null,
          3000,
          "bg-warning"
        );
        return;
      }

      const fatores = this.selectedSensor.calibrationFactors || [];

      // Verifica se todos os campos estão preenchidos
      const algumVazio = fatores.some(f => {
        const valor = this.fatoresValores[f.fator];
        return valor === null || valor === undefined || valor === '';
      });
      if (algumVazio) {
        this.$refs.alert.show(
          "Validação",
          "Todos os campos de calibração devem estar preenchidos",
          null,
          3000,
          "bg-warning"
        );
        return;
      }

      if (!this.isFormValid) {
        this.$refs.alert.show(
          "Validação",
          "Todos os fatores devem ser maiores que zero",
          null,
          3000,
          "bg-warning"
        );
        return;
      }

      // Verifica se os valores são diferentes da calibração ativa do sensor
      const ativa = this.calibracaoAtivaSensor;
      if (ativa && Array.isArray(ativa.fatores)) {
        const valoresIguais = fatores.every(f => {
          const ativaFator = ativa.fatores.find(af => af.fator === f.fator);
          const valorAtivo = ativaFator && ativaFator.valor != null ? ativaFator.valor : 1.0;
          return this.fatoresValores[f.fator] === valorAtivo;
        });

        if (valoresIguais) {
          this.$refs.alert.show(
            "Validação",
            "Você deve alterar pelo menos um dos fatores para salvar uma nova calibração",
            null,
            4000,
            "bg-warning"
          );
          return;
        }
      }

      this.loading = true;
      try {
        const payload = {
          qrCodeId: this.qrCodeId,
          sensorName: this.selectedSensor.linkname,
          fatores: fatores.map(f => ({
            fator: f.fator,
            valor: this.fatoresValores[f.fator]
          }))
        };

        await this.urlPost('/Calibracao', payload);

        this.$refs.alert.show(
          "Sucesso",
          "Calibração salva com sucesso!",
          null,
          5000,
          "bg-success"
        );

        // Recarrega as calibrações
        await this.carregarCalibracoes();

        // Atualiza a datatable de histórico
        if (this.$refs.datatableHistorico) {
          this.$refs.datatableHistorico.update();
        }
      } catch (error) {
        console.error('Erro ao salvar calibração:', error);

        if (error.response && error.response.status === 403) {
          this.$refs.alert.show(
            "Erro de Permissão",
            "Apenas administradores podem salvar calibrações",
            null,
            3000,
            "bg-danger"
          );
        } else {
          this.$refs.alert.show(
            "Erro",
            "Erro ao salvar calibração: " + (error.response?.data?.Message || error.message),
            null,
            3000,
            "bg-danger"
          );
        }
      } finally {
        this.loading = false;
      }
    },
    confirmarDelete(id) {
      // Buscar os dados da calibração na datatable
      const item = this.$refs.datatableHistorico.values.find(v => v.id === id);
      this.deleteItemId = id;
      this.deleteItemData = item || null;

      $(this.$refs.modal_delete).modal({
        backdrop: "static",
        keyboard: false,
      });
    },
    cancelarDelete() {
      this.deleteItemId = null;
      this.deleteItemData = null;
      $(this.$refs.modal_delete).modal("hide");
    },
    async executarDelete() {
      if (!this.deleteItemId) return;

      this.deleting = true;
      try {
        await this.urlDelete(`/Calibracao/${this.deleteItemId}`);

        this.$refs.alert.show(
          "Sucesso",
          "Calibração excluída com sucesso!",
          null,
          3000,
          "bg-success"
        );

        // Fecha o modal de confirmação
        this.cancelarDelete();

        // Recarrega as calibrações
        await this.carregarCalibracoes();

        // Atualiza a datatable de histórico
        if (this.$refs.datatableHistorico) {
          this.$refs.datatableHistorico.update();
        }
      } catch (error) {
        console.error('Erro ao deletar calibração:', error);

        if (error.response && error.response.status === 403) {
          this.$refs.alert.show(
            "Erro de Permissão",
            "Apenas administradores podem deletar calibrações",
            null,
            3000,
            "bg-danger"
          );
        } else {
          this.$refs.alert.show(
            "Erro",
            "Erro ao deletar calibração: " + (error.response?.data?.Message || error.message),
            null,
            3000,
            "bg-danger"
          );
        }
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>

<style scoped>
.sensor-selector-card {
  background-color: #fff;
  border: 1px solid #d9dee3;
  border-radius: 0.375rem;
  padding: 1.5rem;
}

.sensor-selector-card h5 {
  font-size: 1.125rem;
  font-weight: 500;
  color: #566a7f;
  margin-bottom: 1rem;
}

.current-calibration-card {
  background-color: #f8f9fa;
  border: 1px solid #d9dee3;
  border-radius: 0.375rem;
  padding: 1.5rem;
}

.current-calibration-card h5 {
  font-size: 1.125rem;
  font-weight: 500;
  color: #566a7f;
  margin-bottom: 1rem;
}

.new-calibration-form {
  background-color: #fff;
  border: 1px solid #d9dee3;
  border-radius: 0.375rem;
  padding: 1.5rem;
}

.new-calibration-form h5 {
  font-size: 1.125rem;
  font-weight: 500;
  color: #566a7f;
  margin-bottom: 1rem;
}

.historico-container h5 {
  font-size: 1.125rem;
  font-weight: 500;
  color: #566a7f;
  margin-bottom: 1rem;
}
</style>
